import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectAPI, categoryAPI, templateAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Separator } from '../components/ui/separator';
import {
  ArrowLeft, Plus, Trash, Calendar as CalendarIcon, MapPin, Package, CircleNotch,
  PaperPlaneTilt, FloppyDisk, WarningCircle, BookmarkSimple, BookmarksSimple,
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import MatchitoCelebration from '../components/MatchitoCelebration';
import DeliveryMap from '../components/DeliveryMap';
import MaterialImageUpload from '../components/MaterialImageUpload';

const EditProjectPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showMatchito, setShowMatchito] = useState(false);
  const [matchitoMsg, setMatchitoMsg] = useState('');
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '', key: '', description: '', deadline: null,
    delivery_location: { latitude: 19.4326, longitude: -99.1332, radius: 5, address: '' },
  });
  const [materials, setMaterials] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showTemplateName, setShowTemplateName] = useState(false);
  const [templateName, setTemplateName] = useState('');

  useEffect(() => {
    fetchCategories();
    fetchProject();
    fetchTemplates();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await projectAPI.getById(projectId);
      const p = response.data;
      if (p.status !== 'draft') {
        toast.error('Solo se pueden editar proyectos en borrador');
        navigate(`/projects/${projectId}`);
        return;
      }
      setFormData({
        name: p.name || '',
        key: p.key || '',
        description: p.description || '',
        deadline: p.deadline ? new Date(p.deadline) : null,
        delivery_location: p.delivery_location || { latitude: 19.4326, longitude: -99.1332, radius: 5, address: '' },
      });
      const mats = (p.all_materials || p.materials || []).map(m => ({
        name: m.name || '', category: m.category || '', quantity: m.quantity || 1, unit: m.unit || 'pza', image_url: m.image_url || null,
      }));
      setMaterials(mats.length > 0 ? mats : [{ name: '', category: '', quantity: 1, unit: 'pza', image_url: null }]);
    } catch (error) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getAll();
      setCategories(response.data);
    } catch (error) {}
  };

  const fetchTemplates = async () => {
    try { setTemplates((await templateAPI.list()).data || []); } catch { /* ok */ }
  };

  const handleLoadTemplate = (tmpl) => {
    setMaterials(tmpl.materials.map(m => ({ name: m.name, category: m.category, quantity: m.quantity, unit: m.unit, image_url: m.image_url || null })));
    toast.success(`Plantilla "${tmpl.name}" cargada`);
  };

  const handleSaveTemplate = async () => {
    const valid = materials.filter(m => m.name.trim() && m.category);
    if (!valid.length) { toast.error('Agrega materiales válidos primero'); return; }
    if (!templateName.trim()) { toast.error('Ingresa un nombre'); return; }
    setSavingTemplate(true);
    try {
      await templateAPI.create({ name: templateName.trim(), materials: valid.map(m => ({ name: m.name, category: m.category, quantity: m.quantity, unit: m.unit, ...(m.image_url && { image_url: m.image_url }) })) });
      toast.success('Plantilla guardada');
      setShowTemplateName(false);
      setTemplateName('');
      fetchTemplates();
    } catch { toast.error('Error al guardar'); } finally { setSavingTemplate(false); }
  };

  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const handleLocationChange = (field, value) => setFormData(prev => ({
    ...prev, delivery_location: { ...prev.delivery_location, [field]: value }
  }));
  const handleLocationFromMap = (loc) => {
    setFormData(prev => ({
      ...prev,
      delivery_location: {
        latitude: loc.latitude,
        longitude: loc.longitude,
        radius: loc.radius,
        address: loc.address,
      },
    }));
  };
  const handleMaterialChange = (index, field, value) => {
    setMaterials(prev => { const u = [...prev]; u[index] = { ...u[index], [field]: value }; return u; });
  };
  const addMaterial = () => setMaterials(prev => [...prev, { name: '', category: '', quantity: 1, unit: 'pza', image_url: null }]);
  const removeMaterial = (index) => {
    if (materials.length === 1) { toast.error('Debe haber al menos un material'); return; }
    setMaterials(prev => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    if (!formData.name.trim()) { toast.error('El nombre es requerido'); return false; }
    if (!formData.key.trim()) { toast.error('La clave es requerida'); return false; }
    if (!formData.deadline) { toast.error('La fecha límite es requerida'); return false; }
    if (!formData.delivery_location.address.trim()) { toast.error('La dirección es requerida'); return false; }
    for (let i = 0; i < materials.length; i++) {
      const m = materials[i];
      if (!m.name.trim()) { toast.error(`Nombre del material ${i+1} requerido`); return false; }
      if (!m.category) { toast.error(`Categoría del material ${i+1} requerida`); return false; }
      if (m.quantity <= 0) { toast.error(`Cantidad del material ${i+1} debe ser > 0`); return false; }
    }
    return true;
  };

  const handleSave = async (sendImmediately = false) => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const payload = {
        ...formData,
        deadline: formData.deadline.toISOString(),
        materials: materials.map(m => ({
          name: m.name, category: m.category, quantity: parseFloat(m.quantity), unit: m.unit,
          ...(m.image_url && { image_url: m.image_url }),
        })),
      };
      await projectAPI.update(projectId, payload);
      if (sendImmediately) {
        await projectAPI.send(projectId);
        setMatchitoMsg('¡Proyecto actualizado y enviado a proveedores!');
        setShowMatchito(true);
        setTimeout(() => navigate(`/projects/${projectId}`), 4000);
      } else {
        toast.success('Proyecto actualizado');
        navigate(`/projects/${projectId}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><CircleNotch className="w-8 h-8 text-amber-500 animate-spin" /></div>;
  if (notFound) return (
    <div className="text-center py-20">
      <WarningCircle className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
      <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">Proyecto no encontrado</h2>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="edit-project-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(`/projects/${projectId}`)} className="rounded-none">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">Editar Proyecto</h1>
          <p className="text-zinc-600 dark:text-zinc-400 text-sm">{formData.name}</p>
        </div>
      </div>

      {/* Project Info */}
      <Card className="border-zinc-200 dark:border-zinc-700 rounded-none">
        <CardHeader className="border-b border-zinc-200 dark:border-zinc-700">
          <CardTitle>Información del Proyecto</CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nombre del Proyecto *</Label>
              <Input value={formData.name} onChange={(e) => handleInputChange('name', e.target.value)} className="rounded-none" data-testid="edit-name" />
            </div>
            <div className="space-y-2">
              <Label>Clave *</Label>
              <Input value={formData.key} onChange={(e) => handleInputChange('key', e.target.value)} className="rounded-none" data-testid="edit-key" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Descripción</Label>
            <Textarea value={formData.description} onChange={(e) => handleInputChange('description', e.target.value)} className="rounded-none" rows={3} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Fecha Límite *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full rounded-none justify-start text-left font-normal">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {formData.deadline ? format(formData.deadline, 'PPP', { locale: es }) : 'Seleccionar fecha'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-none">
                  <Calendar mode="single" selected={formData.deadline} onSelect={(d) => handleInputChange('deadline', d)} locale={es} />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <Separator />
          <div className="space-y-2">
            <Label>Ubicación de Entrega *</Label>
            <p className="text-xs text-zinc-500">Haz clic en el mapa o busca una dirección para fijar el punto de entrega</p>
            <DeliveryMap
              latitude={formData.delivery_location.latitude}
              longitude={formData.delivery_location.longitude}
              radius={formData.delivery_location.radius}
              address={formData.delivery_location.address}
              onLocationChange={handleLocationFromMap}
            />
          </div>
        </CardContent>
      </Card>

      {/* Materials */}
      <Card className="border-zinc-200 dark:border-zinc-700 rounded-none">
        <CardHeader className="border-b border-zinc-200 dark:border-zinc-700">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" /> Materiales ({materials.length})
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              {templates.length > 0 && (
                <Select onValueChange={(id) => { const t = templates.find(t => t.template_id === id); if (t) handleLoadTemplate(t); }}>
                  <SelectTrigger className="rounded-none w-auto min-w-[160px]" data-testid="template-selector">
                    <BookmarksSimple className="w-4 h-4 mr-1 text-orange-500" />
                    <SelectValue placeholder="Plantilla..." />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {templates.map(t => (
                      <SelectItem key={t.template_id} value={t.template_id}>
                        {t.name} <span className="text-xs text-zinc-400 ml-1">({t.materials_count})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {!showTemplateName ? (
                <Button variant="outline" onClick={() => setShowTemplateName(true)} className="rounded-none text-orange-600 border-orange-200 hover:bg-orange-50" data-testid="save-template-btn">
                  <BookmarkSimple className="w-4 h-4 mr-1" /> Guardar Plantilla
                </Button>
              ) : (
                <div className="flex items-center gap-1.5">
                  <Input value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="Nombre..." className="rounded-none w-36 h-9 text-sm" onKeyDown={(e) => e.key === 'Enter' && handleSaveTemplate()} data-testid="template-name-input" autoFocus />
                  <Button size="sm" onClick={handleSaveTemplate} disabled={savingTemplate} className="rounded-none h-9 bg-orange-500 hover:bg-orange-600" data-testid="template-save-confirm">
                    {savingTemplate ? <CircleNotch className="w-4 h-4 animate-spin" /> : 'Guardar'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setShowTemplateName(false); setTemplateName(''); }} className="rounded-none h-9">&times;</Button>
                </div>
              )}
              <Button variant="outline" onClick={addMaterial} className="rounded-none" data-testid="add-material-btn">
                <Plus className="w-4 h-4 mr-1" /> Agregar
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4 space-y-3">
          {materials.map((material, index) => (
            <div key={index} className="border border-zinc-100 dark:border-zinc-800 p-3 space-y-3">
              <div className="flex gap-3 items-start">
                <MaterialImageUpload
                  imageUrl={material.image_url}
                  onImageChange={(url) => handleMaterialChange(index, 'image_url', url)}
                />
                <div className="flex-1 grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-12 sm:col-span-4">
                    <Label className="text-xs">Material *</Label>
                    <Input value={material.name} onChange={(e) => handleMaterialChange(index, 'name', e.target.value)} className="rounded-none" placeholder="Nombre" data-testid={`material-name-${index}`} />
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <Label className="text-xs">Categoría *</Label>
                    <Select value={material.category} onValueChange={(v) => handleMaterialChange(index, 'category', v)}>
                      <SelectTrigger className="rounded-none"><SelectValue placeholder="Categoría" /></SelectTrigger>
                      <SelectContent>
                        {categories.map(c => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3 sm:col-span-2">
                    <Label className="text-xs">Cantidad *</Label>
                    <Input type="number" value={material.quantity} onChange={(e) => handleMaterialChange(index, 'quantity', e.target.value)} className="rounded-none" min="0.01" step="0.01" />
                  </div>
                  <div className="col-span-2 sm:col-span-2">
                    <Label className="text-xs">Unidad</Label>
                    <Select value={material.unit} onValueChange={(v) => handleMaterialChange(index, 'unit', v)}>
                      <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['pza','kg','m','m2','m3','l','ton','bulto','saco','rollo','caja','paquete','juego'].map(u => (
                          <SelectItem key={u} value={u}>{u}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-1">
                    <Button variant="ghost" size="icon" onClick={() => removeMaterial(index)} className="text-red-500 hover:text-red-700 h-9 w-9">
                      <Trash className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row justify-end gap-3">
        <Button variant="outline" onClick={() => navigate(`/projects/${projectId}`)} className="rounded-none">
          Cancelar
        </Button>
        <Button variant="outline" onClick={() => handleSave(false)} disabled={saving} className="rounded-none" data-testid="save-draft-btn">
          <FloppyDisk className="w-4 h-4 mr-2" />
          {saving ? 'Guardando...' : 'Guardar Borrador'}
        </Button>
        <Button onClick={() => handleSave(true)} disabled={saving} className="btn-primary rounded-none" data-testid="save-and-send-btn">
          <PaperPlaneTilt className="w-4 h-4 mr-2" />
          {saving ? 'Enviando...' : 'Guardar y Enviar'}
        </Button>
      </div>

      <MatchitoCelebration show={showMatchito} message={matchitoMsg} onClose={() => setShowMatchito(false)} />
    </div>
  );
};

export default EditProjectPage;
